import { useCallback, useLayoutEffect, useRef } from 'react';

const ROW_REORDER_DURATION_MS = 500;

// CSS for the price-flash highlight. Exported so the table can inject it inside
// its own <style> block, keeping every animation concern in this one place.
export const PRICE_FLASH_STYLES = `
  @keyframes price-flash {
    0% { background-color: transparent; }
    25% { background-color: color-mix(in srgb, var(--color-status-warning) 50%, transparent); }
    100% { background-color: transparent; }
  }
  .price-flash {
    animation: price-flash 700ms ease-out;
    border-radius: 4px;
    padding: 0 2px;
    margin: 0 -2px;
  }
`;

// Honours the user's OS "reduce motion" accessibility setting; when true we skip
// animations and snap straight to the final state.
export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export type RegisterRow = (
  rowId: string,
  rowElement: HTMLTableRowElement | null
) => void;

// FLIP reorder animation. Returns a ref callback to attach to every row; on each
// render where `orderKey` changes, rows slide from the layout position they held
// on the previous render to their new one instead of jumping there instantly.
export const useRowReorderAnimation = (orderKey: unknown): RegisterRow => {
  // Maps each row id to its <tr> element, plus the layout position it held on
  // the previous render — together these drive the FLIP reorder animation.
  const rowElementsRef = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const previousRowOffsetsRef = useRef<Map<string, number>>(new Map());

  useLayoutEffect(() => {
    const previousOffsets = previousRowOffsetsRef.current;
    const currentOffsets = new Map<string, number>();
    rowElementsRef.current.forEach((rowElement, rowId) => {
      // offsetTop is a layout-only measurement, so it ignores any transform
      // still in flight from a previous animation.
      currentOffsets.set(rowId, rowElement.offsetTop);
    });

    if (!prefersReducedMotion()) {
      rowElementsRef.current.forEach((rowElement, rowId) => {
        const previousOffset = previousOffsets.get(rowId);
        const currentOffset = currentOffsets.get(rowId);
        if (previousOffset === undefined || currentOffset === undefined) return;

        const offsetDelta = previousOffset - currentOffset;
        if (offsetDelta === 0) return;

        // Invert: place the row back at its old position with no transition...
        rowElement.style.transition = 'none';
        rowElement.style.transform = `translateY(${offsetDelta}px)`;
        // ...force a reflow so the browser registers that starting point...
        void rowElement.offsetHeight;

        // ...then play: transition the transform away so it glides into place.
        requestAnimationFrame(() => {
          rowElement.style.transition = `transform ${ROW_REORDER_DURATION_MS}ms ease`;
          rowElement.style.transform = '';
          const handleTransitionEnd = () => {
            rowElement.style.transition = '';
            rowElement.removeEventListener(
              'transitionend',
              handleTransitionEnd
            );
          };
          rowElement.addEventListener('transitionend', handleTransitionEnd);
        });
      });
    }

    previousRowOffsetsRef.current = currentOffsets;
  }, [orderKey]);

  return useCallback<RegisterRow>((rowId, rowElement) => {
    if (rowElement) {
      rowElementsRef.current.set(rowId, rowElement);
    } else {
      rowElementsRef.current.delete(rowId);
    }
  }, []);
};
